import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { CaretRightFilled, CaretDownFilled } from '@ant-design/icons';
import { Input, Select, Collapse, Divider } from 'antd';
import classNames from 'classnames';
import { noop, isEmpty } from 'lodash';

import {
  groupUpdateAttribute,
  groupRemoveAttribute,
  conjointMoveAttribute,
} from 'containers/QuestionnairePage/actions';

import { deleteMedia } from 'containers/App/actions';

import { moveNodeOnList } from 'containers/QuestionnairePage/utils';

import conceptMessages from 'containers/QuestionnairePage/QuestionGroup/ABTest/Concepts/messages';
import { MOVE_DIRECTION } from 'containers/QuestionnairePage/constants';
import { WithPermissions, EDIT_PERMISSIONS } from 'containers/Permissions';

import Pficon from 'components/Pficon';
import PfAlert from 'components/PfAlert';
import { TYPE_DANGER } from 'components/PfAlert/constants';

import { useDebounce, useDidUpdate } from 'utils/hooks';
import { isMaxDiffGroup } from 'utils/nodeTypes';

import { WithOptionalDrag } from 'components/DragAndDrop/Card';
import { DRAG_TYPE_ATTRIBUTE_LEVEL } from 'components/DragAndDrop/constants';
import { getAnswerDragDisplayLabel } from 'containers/QuestionnairePage/selectorUtils';

import {
  emptyAttributeValuesSelector,
  getConjointAttributeValuesOnAdd,
  attachEmptyConjointAttributeValue,
  hasConjointEmptyAttributeValues,
  getConjointNumericValue,
  hasMixedPositiveWithNegativePriceValues,
  hasSameAttributePriceValues,
} from 'containers/QuestionnairePage/QuestionGroup/utils';
import { hasExceededMaxAttributeValuesLimit } from './utils';
import {
  CONJOINT_ATTRIBUTE_TYPES,
  CONJOINT_TEXT_MAX_LENGTH,
  COLLAPSE_OPEN_KEY,
  CONJOINT_MIN_ATTRIBUTE_VALUES,
} from './constants';
import messages from './messages';
import AttributeLevel from './AttributeLevel';
import AttributeActions from './AttributeActions';

const PanelCollapse = Collapse.Panel;

function Attribute(props) {
  const {
    id,
    media,
    surveyId,
    refId: attributeRefId,
    maxAttributeValues,
    hideActions,
    pristineValues: { isPristineAttributeValues },
  } = props;
  const [name, setName] = useState(props.name);
  const [isCollapsed, setCollapsed] = useState(false);
  const [attributeValues, setAttributeValues] = useState(
    attachEmptyConjointAttributeValue(props.values),
  );
  const onChangeName = useCallback((e) => setName(e.target.value), []);
  const dispatch = useDispatch();
  const emptyValues = emptyAttributeValuesSelector(attributeValues);
  const hasEmptyValue = emptyValues.length > 0;
  const hasExceededMaxLimit = hasExceededMaxAttributeValuesLimit(
    attributeValues,
    maxAttributeValues,
    emptyValues,
  );

  const onRemove = useCallback(() => {
    dispatch(
      groupRemoveAttribute({
        refId: props.groupRefId,
        attributeRefId,
        groupType: props.groupType,
      }),
    );

    if (media && media.type) {
      onRemoveMedia({
        mediaType: media.type,
        refId: attributeRefId,
      });
    }

    attributeValues.forEach((attributeValue) => {
      if (attributeValue.media && attributeValue.media.type) {
        onRemoveMedia({
          mediaType: attributeValue.media.type,
          refId: attributeValue.refId,
        });
      }
    });
  }, [media, attributeValues]);

  const onMoveUp = useCallback(() => {
    dispatch(
      conjointMoveAttribute({
        direction: MOVE_DIRECTION.UP,
        refId: props.groupRefId,
        attributeRefId,
      }),
    );
  }, []);

  const onMoveDown = useCallback(() => {
    dispatch(
      conjointMoveAttribute({
        direction: MOVE_DIRECTION.DOWN,
        refId: props.groupRefId,
        attributeRefId,
      }),
    );
  }, []);

  const updateAttribute = useCallback((payload) => {
    dispatch(
      groupUpdateAttribute({
        refId: props.groupRefId,
        attributeRefId,
        groupType: props.groupType,
        ...payload,
      }),
    );
  }, []);

  /* const onChangeSortable = useCallback((e) => {
    updateAttribute({
      sortable: e.target.checked,
    });
  }, []); */

  const [debouncedName] = useDebounce(name, 500);
  useDidUpdate(() => {
    updateAttribute({
      name: debouncedName,
      pristineValues: {
        ...props.pristineValues,
        isPristineAttributes: false,
      },
    });
  }, [debouncedName]);

  const onSelectType = useCallback(
    (type) => {
      if (type === props.type) {
        return;
      }

      const typeWasPrice = props.type === CONJOINT_ATTRIBUTE_TYPES.PRICE;
      const typeIsPrice = type === CONJOINT_ATTRIBUTE_TYPES.PRICE;
      const payload = {
        type,
      };

      if (typeWasPrice || typeIsPrice) {
        const newValues = attributeValues.map((value) => ({
          ...value,
          numericValue: getConjointNumericValue(value.text, type),
        }));

        setAttributeValues(newValues);

        payload.values = newValues.filter((value) => !value.isEmpty);
      }

      updateAttribute(payload);
    },
    [props.type, attributeValues],
  );

  const toggleCollapse = useCallback(() => {
    setCollapsed(!isCollapsed);
  }, [isCollapsed]);

  const renderCollapseIcon = () => {
    const Icon = isCollapsed ? CaretRightFilled : CaretDownFilled;
    return (
      <span>
        <Icon onClick={toggleCollapse} className="color-grey-10 text-xs" />
      </span>
    );
  };

  const onUpdateAttributeValues = useCallback(
    ({
      values,
      previousValues,
      removedAttributeValues,
      pristineValues,
      shouldUpdate = true,
    }) => {
      setAttributeValues(values);

      if (shouldUpdate) {
        updateAttribute({
          values: values.filter((value) => !value.isEmpty),
          previousValues: previousValues.filter((value) => !value.isEmpty),
          removedAttributeValues,
          pristineValues: pristineValues || props.pristineValues,
        });
      }
    },
    [setAttributeValues, updateAttribute, props.pristineValues],
  );

  const onAddLevel = useCallback(
    (attributeValueRefId) => {
      const { newValues, shouldUpdate } = getConjointAttributeValuesOnAdd(
        attributeValues,
        attributeValueRefId,
      );

      onUpdateAttributeValues({
        values: newValues,
        previousValues: attributeValues,
        shouldUpdate,
      });
    },
    [hasEmptyValue, attributeValues],
  );

  const onUpdateLevel = useCallback(
    (attributeValueRefId, payload) => {
      let wasEmpty;
      let newValues = attributeValues.map((attributeValue) => {
        if (attributeValue.refId === attributeValueRefId) {
          wasEmpty = attributeValue.isEmpty;

          return {
            ...attributeValue,
            ...payload,
            isEmpty: false,
          };
        }

        return attributeValue;
      });

      if (wasEmpty) {
        newValues = attachEmptyConjointAttributeValue(newValues);
      }

      onUpdateAttributeValues({
        values: newValues,
        previousValues: attributeValues,
        pristineValues: {
          ...props.pristineValues,
          isPristineAttributeValues: false,
        },
      });
    },
    [hasEmptyValue, attributeValues, props.pristineValues],
  );

  const onMoveLevel = useCallback(
    (attributeValueRefId, direction) => {
      const { newNodes: newValues } = moveNodeOnList(
        attributeValues,
        attributeValueRefId,
        direction,
      );

      onUpdateAttributeValues({
        values: newValues,
        previousValues: attributeValues,
      });
    },
    [attributeValues],
  );

  const onMoveLevelByDragging = useCallback(
    ({ refId, direction }) => {
      onMoveLevel(refId, direction);
    },
    [onMoveLevel],
  );

  const onRemoveLevel = useCallback(
    (attributeValueRefId) => {
      const newValues = attributeValues.filter(
        (attributeValue) => attributeValue.refId !== attributeValueRefId,
      );

      onUpdateAttributeValues({
        values: newValues,
        previousValues: attributeValues,
        removedAttributeValues: [attributeValueRefId],
      });
    },
    [attributeValues],
  );

  // surveyId, refId, mediaType
  const onRemoveMedia = useCallback(
    (payload) => {
      dispatch(
        deleteMedia({
          ...payload,
          surveyId,
        }),
      );
    },
    [surveyId],
  );

  const addAttributeImage = useCallback(
    ({ url, thumbnail, type, filename } = {}) => {
      if (!url) {
        updateAttribute({
          media: undefined,
        });
        return;
      }

      updateAttribute({
        media: {
          url,
          thumbnail,
          type,
          filename,
        },
      });
    },
    [updateAttribute],
  );

  const removeAttributeImage = useCallback(() => {
    onRemoveMedia({
      mediaType: media.type,
      refId: attributeRefId,
    });
    updateAttribute({
      media: undefined,
    });
  }, [media, onRemoveMedia]);

  const emptyAttributeValues = useMemo(
    () => hasConjointEmptyAttributeValues(attributeValues, props.type),
    [attributeValues, props.type],
  );

  return (
    <WithPermissions
      permission={[
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_NAME,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_TYPE,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_SORTABLE,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_ADD,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_REORDER,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_REMOVE,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_EDIT_MEDIA,
      ]}
      itemToCheck={{ id }}
    >
      {(
        _,
        [
          hasAttributeNameEditPermission,
          hasAttributeTypeEditPermission,
          // hasAttributeSortableEditPermission,
          hasAttributeValueAddPermission,
          hasAttributeReOrderPermission,
          hasAttributeRemovePermission,
          hasAttributeEditMediaPermission,
        ],
      ) => (
        <div className="background-grey-1 border-radius-level-1 padding-right-xl padding-vertical-md margin-top-sm">
          <Collapse
            bordered={false}
            className="padding-top-none pf-ant-collapse ant-collapse-question attribute-group-card"
            style={{ paddingLeft: 32 }}
            activeKey={isCollapsed ? null : COLLAPSE_OPEN_KEY}
            expandIcon={renderCollapseIcon}
          >
            <PanelCollapse
              key={COLLAPSE_OPEN_KEY}
              header={
                <div
                  className="padding-left-md position-relative"
                  style={{
                    top: 4,
                  }}
                >
                  <div className="flex flex-items-center flex-justify-between">
                    <p className="text-weight-bold text-size-secondary color-grey-10 margin-none">
                      <FormattedMessage
                        {...conceptMessages.attributePlaceholder}
                        values={{ index: props.index + 1 }}
                      />
                    </p>
                    {!hideActions && (
                      <div className="flex flex-items-center">
                        <AttributeActions
                          moveUpDisabled={props.moveUpDisabled}
                          moveDownDisabled={props.moveDownDisabled}
                          removeDisabled={props.removeDisabled}
                          onMoveDown={onMoveDown}
                          onMoveUp={onMoveUp}
                          onRemove={onRemove}
                          permissions={[
                            hasAttributeReOrderPermission,
                            hasAttributeRemovePermission,
                            hasAttributeEditMediaPermission,
                          ]}
                          dataTest={`attribute-${props.index + 1}`}
                          media={props.media}
                          refId={attributeRefId}
                          surveyId={surveyId}
                          index={props.index + 1}
                          uploadUrl={`/api/v3/media/draft-survey/${surveyId}?refId=${attributeRefId}`}
                          addMedia={addAttributeImage}
                          removeMedia={removeAttributeImage}
                          hideActions={hideActions}
                          canHaveMoreThanOneAttribute={
                            !isMaxDiffGroup(props.groupType)
                          }
                          isMaxDiff={isMaxDiffGroup(props.groupType)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              }
            >
              <div className="margin-top-md padding-btm-md">
                <div
                  className="flex flex-items-center"
                  style={{
                    width: 'calc(100% - 120px)',
                  }}
                >
                  <div className="flex-1">
                    <p className="text-weight-bold text-size-10 color-grey-10 margin-btm-tiny">
                      <FormattedMessage {...messages.attributeName} />
                    </p>
                    <Input
                      spellCheck={false}
                      readOnly={!hasAttributeNameEditPermission}
                      value={name}
                      onChange={onChangeName}
                      maxLength={CONJOINT_TEXT_MAX_LENGTH}
                      style={{ height: 30 }}
                      autoFocus={props.focus}
                      className={classNames(
                        'ant-input-shadow-none',
                        'text-size-secondary',
                        'pf-input-no-shadow',
                        'padding-horizontal-xs',
                        'border-none',
                        'outline-none',
                        'shadow-none',
                        'border-radius-level-1',
                        'cursor-text',
                        'color-grey-10',
                        'color-grey-9-placeholder',
                        'background-grey-4',
                      )}
                    />
                  </div>

                  <Divider
                    type="vertical"
                    className="margin-horizontal-xs color-grey-5"
                    style={{
                      marginTop: 20,
                    }}
                  />

                  <div>
                    <p className="text-weight-bold text-size-10 color-grey-10 margin-btm-tiny">
                      <FormattedMessage {...messages.attributeType} />
                    </p>
                    <Select
                      getPopupContainer={(trigger) => trigger.parentNode}
                      className="pf-select pf-select--text-normal pf-select--small w-full conjoint-select border-radius-level-1"
                      value={props.type}
                      disabled={!hasAttributeTypeEditPermission}
                      onSelect={onSelectType}
                      suffixIcon={<Pficon type="keyboard_arrow_down" />}
                    >
                      <Select.Option
                        value={CONJOINT_ATTRIBUTE_TYPES.FEATURE}
                        key={`${attributeRefId}-${
                          CONJOINT_ATTRIBUTE_TYPES.FEATURE
                        }`}
                      >
                        <FormattedMessage
                          {...messages.attributeTypes.feature}
                        />
                      </Select.Option>
                      {/* <Select.Option
                        value={CONJOINT_ATTRIBUTE_TYPES.BRAND}
                        key={`${attributeRefId}-${CONJOINT_ATTRIBUTE_TYPES.BRAND}`}
                      >
                        <FormattedMessage {...messages.attributeTypes.brand} />
                      </Select.Option> */}
                      <Select.Option
                        value={CONJOINT_ATTRIBUTE_TYPES.PRICE}
                        key={`${attributeRefId}-${
                          CONJOINT_ATTRIBUTE_TYPES.PRICE
                        }`}
                      >
                        <FormattedMessage {...messages.attributeTypes.price} />
                      </Select.Option>
                    </Select>
                  </div>
                </div>

                {!props.pristineValues.isPristineAttributes &&
                  isEmpty(name) && (
                    <PfAlert
                      className="margin-top-xxs"
                      noBackground
                      noPadding
                      title={
                        <FormattedMessage
                          {...messages.errors.emptyAttributeName}
                        />
                      }
                      type={TYPE_DANGER}
                      inlineStyle={false}
                    />
                  )}

                {/* <Checkbox
                  id={`sortable-${attributeRefId}`}
                  className={classNames(
                    'ant-checkbox-dark',
                    'margin-top-tiny',
                    {
                      'background-transparent': !hasAttributeSortableEditPermission,
                      'background-transparent-hover': !hasAttributeSortableEditPermission,
                      'color-grey-7': !hasAttributeSortableEditPermission,
                    },
                  )}
                  checked={props.sortable}
                  onChange={onChangeSortable}
                  disabled={!hasAttributeSortableEditPermission}
                />
                <label
                  htmlFor={`sortable-${attributeRefId}`}
                  className={classNames(
                    'margin-horizontal-xs',
                    'text-size-secondary',
                    {
                      'cursor-pointer': hasAttributeSortableEditPermission,
                      'color-grey-10': hasAttributeSortableEditPermission,
                      'background-transparent': !hasAttributeSortableEditPermission,
                      'background-transparent-hover': !hasAttributeSortableEditPermission,
                      'color-grey-7': !hasAttributeSortableEditPermission,
                    },
                  )}
                >
                  <FormattedMessage {...messages.sortable} />
                </label> */}

                <p className="text-weight-bold text-size-10 color-grey-10 margin-top-xs margin-btm-none">
                  <FormattedMessage {...messages.attributeLevels} />
                </p>

                {attributeValues
                  // NOTE: this might be buggy if there are cases where we need the empty fields to show
                  //       even when we don't have the proper permission
                  .filter((attributeValue) => {
                    if (
                      !hasAttributeValueAddPermission &&
                      attributeValue.isEmpty
                    ) {
                      return false;
                    }
                    return true;
                  })
                  .map((value, index) => {
                    const attributeLevel = (
                      <AttributeLevel
                        {...value}
                        attributeType={props.type}
                        moveUpDisabled={index === 0}
                        moveDownDisabled={
                          index ===
                          attributeValues.length - 1 - emptyValues.length
                        }
                        removeDisabled={
                          // we have one empty attribute value
                          attributeValues.length - 1 ===
                          CONJOINT_MIN_ATTRIBUTE_VALUES
                        }
                        onAddLevel={
                          hasAttributeValueAddPermission ? onAddLevel : noop
                        }
                        onRemoveLevel={onRemoveLevel}
                        onUpdateLevel={onUpdateLevel}
                        onMoveLevel={onMoveLevel}
                        surveyId={surveyId}
                        onRemoveMedia={onRemoveMedia}
                        dataTest={`attribute-level-${props.index + 1}-${
                          index + 1
                        }`}
                        focus={props.focus}
                        hideActions={hideActions}
                      />
                    );

                    if (value.isEmpty || hideActions) {
                      return (
                        <React.Fragment key={value.refId}>
                          {attributeLevel}
                        </React.Fragment>
                      );
                    }

                    return (
                      <WithPermissions
                        key={value.refId}
                        permission={
                          EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_REORDER
                        }
                        itemToCheck={{ id: value.id }}
                      >
                        {(hasLevelReorderPermission) => (
                          <WithOptionalDrag
                            item={{
                              id: value.refId,
                              index,
                              type: DRAG_TYPE_ATTRIBUTE_LEVEL,
                              // reuse groupRefId for handleMove same-list scoping
                              groupRefId: attributeRefId,
                              attributeRefId,
                              prompt: value.text,
                              answerIndex: getAnswerDragDisplayLabel(
                                value,
                                index,
                              ),
                              acceptDrag: true,
                              acceptDrop: true,
                            }}
                            onDrop={onMoveLevelByDragging}
                            disabled={!hasLevelReorderPermission}
                          >
                            {attributeLevel}
                          </WithOptionalDrag>
                        )}
                      </WithPermissions>
                    );
                  })}

                {!isPristineAttributeValues && emptyAttributeValues && (
                  <PfAlert
                    className="margin-top-xxs"
                    noBackground
                    noPadding
                    title={
                      <FormattedMessage
                        {...messages.errors.emptyAttributeLevels}
                      />
                    }
                    type={TYPE_DANGER}
                    inlineStyle={false}
                  />
                )}

                {!isPristineAttributeValues && hasExceededMaxLimit && (
                  <PfAlert
                    className="margin-top-xxs"
                    noBackground
                    noPadding
                    title={
                      <FormattedMessage
                        values={{
                          maxLimit: maxAttributeValues,
                        }}
                        {...messages.errors.exceededMaxLimit}
                      />
                    }
                    type={TYPE_DANGER}
                    inlineStyle={false}
                  />
                )}

                {!isPristineAttributeValues &&
                  !emptyAttributeValues &&
                  hasMixedPositiveWithNegativePriceValues(
                    attributeValues,
                    props.type,
                  ) && (
                    <PfAlert
                      className="margin-top-xxs"
                      noBackground
                      noPadding
                      title={
                        <FormattedMessage
                          {...messages.errors
                            .mixedPositiveWithNegativePriceValues}
                        />
                      }
                      type={TYPE_DANGER}
                      inlineStyle={false}
                    />
                  )}

                {!isPristineAttributeValues &&
                  !emptyAttributeValues &&
                  hasSameAttributePriceValues(attributeValues, props.type) && (
                    <PfAlert
                      className="margin-top-xxs"
                      noBackground
                      noPadding
                      title={
                        <FormattedMessage
                          {...messages.errors.sameAttributePriceValues}
                        />
                      }
                      type={TYPE_DANGER}
                      inlineStyle={false}
                    />
                  )}
              </div>
            </PanelCollapse>
          </Collapse>
        </div>
      )}
    </WithPermissions>
  );
}

Attribute.propTypes = {
  refId: PropTypes.string,
  groupRefId: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  sortable: PropTypes.bool,
  values: PropTypes.array,
  index: PropTypes.number,
  moveUpDisabled: PropTypes.bool,
  moveDownDisabled: PropTypes.bool,
  removeDisabled: PropTypes.bool,
  focus: PropTypes.bool,
  surveyId: PropTypes.number,
  maxAttributeValues: PropTypes.number,
};

export default Attribute;
